import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RefreshCw, Filter, Calculator, TrendingUp, TrendingDown, LineChart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OptionChain = ({ optionData: initialOptionData, symbol, currentPrice, onVisualizePL }) => {
  const [activeType, setActiveType] = useState('calls');
  const [optionData, setOptionData] = useState(initialOptionData);
  const [selectedExpiration, setSelectedExpiration] = useState(initialOptionData?.current_expiration || '');
  const [selectedStrike, setSelectedStrike] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showProfitCalculations, setShowProfitCalculations] = useState(true);
  const [positionType, setPositionType] = useState('long'); // 'long' or 'short'
  const [selectedOptions, setSelectedOptions] = useState([]); // For visualize P/L

  useEffect(() => {
    if (initialOptionData) {
      setOptionData(initialOptionData);
      setSelectedExpiration(initialOptionData.current_expiration);
    }
  }, [initialOptionData]);

  const loadOptionChainForExpiration = async (expiration) => {
    if (!symbol || !expiration) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/options/${symbol}?expiration=${expiration}`);
      setOptionData(response.data);
      setSelectedExpiration(expiration);
      toast.success(`Loaded options for ${expiration}`);
    } catch (error) {
      console.error('Error loading option chain:', error);
      toast.error('Failed to load option chain');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return typeof num === 'number' ? num.toFixed(2) : num;
  };

  // Get unique strike prices
  const getUniqueStrikes = () => {
    if (!optionData) return [];
    const strikes = new Set();
    optionData.calls?.forEach(opt => strikes.add(opt.strike));
    optionData.puts?.forEach(opt => strikes.add(opt.strike));
    return Array.from(strikes).sort((a, b) => a - b);
  };

  // Calculate profit/loss for options (both long and short)
  const calculateProfitLoss = (option, isCall, currentStockPrice, isLong = true) => {
    if (!currentStockPrice || !option.lastPrice) return { breakeven: 'N/A', maxProfit: 'N/A', maxLoss: 'N/A', profitAt10: 'N/A', profitAt20: 'N/A' };
    
    const strike = option.strike;
    const premium = option.lastPrice;
    
    if (isCall) {
      if (isLong) {
        // LONG CALL
        const breakeven = strike + premium;
        const maxLoss = premium * 100;
        const profit10 = Math.max(0, (currentStockPrice * 1.1) - strike - premium) * 100;
        const profit20 = Math.max(0, (currentStockPrice * 1.2) - strike - premium) * 100;
        
        return {
          breakeven: breakeven.toFixed(2),
          maxProfit: 'Unlimited',
          maxLoss: maxLoss.toFixed(0),
          profitAt10: profit10.toFixed(0),
          profitAt20: profit20.toFixed(0),
          profitAt10Pct: profit10 > 0 ? ((profit10 / (premium * 100)) * 100).toFixed(1) : '0.0',
          profitAt20Pct: profit20 > 0 ? ((profit20 / (premium * 100)) * 100).toFixed(1) : '0.0'
        };
      } else {
        // SHORT CALL
        const breakeven = strike + premium;
        const maxProfit = premium * 100;
        const loss10 = Math.max(0, (currentStockPrice * 1.1) - strike - premium) * -100; // Loss when stock goes up
        const loss20 = Math.max(0, (currentStockPrice * 1.2) - strike - premium) * -100;
        
        return {
          breakeven: breakeven.toFixed(2),
          maxProfit: maxProfit.toFixed(0),
          maxLoss: 'Unlimited',
          profitAt10: loss10.toFixed(0),
          profitAt20: loss20.toFixed(0),
          profitAt10Pct: loss10 < 0 ? ((Math.abs(loss10) / (premium * 100)) * 100).toFixed(1) : '0.0',
          profitAt20Pct: loss20 < 0 ? ((Math.abs(loss20) / (premium * 100)) * 100).toFixed(1) : '0.0'
        };
      }
    } else {
      // PUT OPTIONS
      if (isLong) {
        // LONG PUT
        const breakeven = strike - premium;
        const maxLoss = premium * 100;
        const profit10 = Math.max(0, strike - (currentStockPrice * 0.9) - premium) * 100;
        const profit20 = Math.max(0, strike - (currentStockPrice * 0.8) - premium) * 100;
        
        return {
          breakeven: breakeven.toFixed(2),
          maxProfit: ((strike - premium) * 100).toFixed(0),
          maxLoss: maxLoss.toFixed(0),
          profitAt10: profit10.toFixed(0),
          profitAt20: profit20.toFixed(0),
          profitAt10Pct: profit10 > 0 ? ((profit10 / (premium * 100)) * 100).toFixed(1) : '0.0',
          profitAt20Pct: profit20 > 0 ? ((profit20 / (premium * 100)) * 100).toFixed(1) : '0.0'
        };
      } else {
        // SHORT PUT
        const breakeven = strike - premium;
        const maxProfit = premium * 100;
        const loss10 = Math.max(0, strike - (currentStockPrice * 0.9) - premium) * -100; // Loss when stock goes down
        const loss20 = Math.max(0, strike - (currentStockPrice * 0.8) - premium) * -100;
        
        return {
          breakeven: breakeven.toFixed(2),
          maxProfit: maxProfit.toFixed(0),
          maxLoss: ((strike - premium) * 100).toFixed(0),
          profitAt10: loss10.toFixed(0),
          profitAt20: loss20.toFixed(0),
          profitAt10Pct: loss10 < 0 ? ((Math.abs(loss10) / (premium * 100)) * 100).toFixed(1) : '0.0',
          profitAt20Pct: loss20 < 0 ? ((Math.abs(loss20) / (premium * 100)) * 100).toFixed(1) : '0.0'
        };
      }
    }
  };

  // Filter options by selected strike
  const filterByStrike = (options) => {
    if (selectedStrike === 'all') return options;
    return options.filter(opt => opt.strike === parseFloat(selectedStrike));
  };

  const renderOptionTable = (options, isCall = true) => {
    if (!currentPrice) {
      return (
        <div className="text-center py-8 text-gray-500">
          Current stock price not available for profit calculations
        </div>
      );
    }

    const isLong = positionType === 'long';

    return (
      <ScrollArea className="h-96 w-full rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead className="bg-gradient-to-r from-indigo-50 to-purple-50 sticky top-0">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Strike</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Last</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Bid/Ask</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Volume</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">IV</th>
                {showProfitCalculations && (
                  <>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">Break-Even</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">Max {isLong ? 'Loss' : 'Profit'}</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">Max {isLong ? 'Profit' : 'Loss'}</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">
                      {isCall ? (isLong ? '+10% Stock' : 'Stock +10%') : (isLong ? '-10% Stock' : 'Stock -10%')}
                    </th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">
                      {isCall ? (isLong ? '+20% Stock' : 'Stock +20%') : (isLong ? '-20% Stock' : 'Stock -20%')}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {options.map((option, index) => {
                const calculations = showProfitCalculations ? calculateProfitLoss(option, isCall, currentPrice, isLong) : null;
                const isITM = isCall ? option.strike < currentPrice : option.strike > currentPrice;
                
                return (
                  <tr 
                    key={index} 
                    className={`border-b hover:bg-indigo-50 hover:bg-opacity-50 transition-colors ${
                      isITM ? 'bg-green-50 bg-opacity-30' : 'bg-white'
                    }`}
                    data-testid={`option-row-${index}`}
                  >
                    <td className="px-3 py-3 font-semibold text-gray-900">
                      <div className="flex items-center">
                        ${formatNumber(option.strike)}
                        {isITM && <Badge className="ml-2 text-xs bg-green-500">ITM</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-700 font-semibold">${formatNumber(option.lastPrice)}</td>
                    <td className="px-3 py-3 text-gray-700">
                      <div className="text-xs">
                        <div>${formatNumber(option.bid)}</div>
                        <div>${formatNumber(option.ask)}</div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-700">{option.volume || 0}</td>
                    <td className="px-3 py-3 text-gray-700">{formatNumber(option.impliedVolatility * 100)}%</td>
                    
                    {showProfitCalculations && calculations && (
                      <>
                        <td className="px-3 py-3 text-gray-700 font-medium">${calculations.breakeven}</td>
                        <td className="px-3 py-3 font-medium">
                          <span className={isLong ? 'text-red-600' : 'text-green-600'}>
                            {isLong ? '-' : '+'}${calculations[isLong ? 'maxLoss' : 'maxProfit']}
                          </span>
                        </td>
                        <td className="px-3 py-3 font-medium">
                          <span className={isLong ? 'text-green-600' : 'text-red-600'}>
                            {calculations[isLong ? 'maxProfit' : 'maxLoss'] === 'Unlimited' ? 'Unlimited' : 
                             (isLong ? '+' : '-') + '$' + calculations[isLong ? 'maxProfit' : 'maxLoss']}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-xs">
                            <div className={`font-medium ${
                              isLong 
                                ? (calculations.profitAt10 > 0 ? 'text-green-600' : 'text-gray-500')
                                : (calculations.profitAt10 < 0 ? 'text-red-600' : 'text-gray-500')
                            }`}>
                              {isLong ? '' : calculations.profitAt10 < 0 ? '-' : '+'}${Math.abs(calculations.profitAt10)}
                            </div>
                            <div className={`text-xs ${
                              isLong
                                ? (calculations.profitAt10 > 0 ? 'text-green-600' : 'text-gray-400')
                                : (calculations.profitAt10 < 0 ? 'text-red-600' : 'text-gray-400')
                            }`}>
                              {calculations.profitAt10Pct}%
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-xs">
                            <div className={`font-medium ${
                              isLong
                                ? (calculations.profitAt20 > 0 ? 'text-green-600' : 'text-gray-500')
                                : (calculations.profitAt20 < 0 ? 'text-red-600' : 'text-gray-500')
                            }`}>
                              {isLong ? '' : calculations.profitAt20 < 0 ? '-' : '+'}${Math.abs(calculations.profitAt20)}
                            </div>
                            <div className={`text-xs ${
                              isLong
                                ? (calculations.profitAt20 > 0 ? 'text-green-600' : 'text-gray-400')
                                : (calculations.profitAt20 < 0 ? 'text-red-600' : 'text-gray-400')
                            }`}>
                              {calculations.profitAt20Pct}%
                            </div>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ScrollArea>
    );
  };

  const uniqueStrikes = getUniqueStrikes();

  if (!optionData) return null;

  return (
    <div className="space-y-4" data-testid="option-chain-container">
      {/* Filters Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
        <div className="flex items-center mb-3">
          <Filter className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">Filter Options</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Expiration Date Selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Expiration Date</label>
            <Select 
              value={selectedExpiration} 
              onValueChange={loadOptionChainForExpiration}
              disabled={loading}
            >
              <SelectTrigger data-testid="expiration-select" className="bg-white">
                <SelectValue placeholder="Select expiration" />
              </SelectTrigger>
              <SelectContent>
                {optionData.expirations?.map((exp) => (
                  <SelectItem key={exp} value={exp} data-testid={`expiration-${exp}`}>
                    {exp}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {optionData.expirations?.length || 0} expirations available
            </p>
          </div>

          {/* Strike Price Filter */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Strike Price</label>
            <Select 
              value={selectedStrike} 
              onValueChange={setSelectedStrike}
            >
              <SelectTrigger data-testid="strike-select" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all" data-testid="strike-all">All Strikes</SelectItem>
                {uniqueStrikes.map((strike) => (
                  <SelectItem key={strike} value={strike.toString()} data-testid={`strike-${strike}`}>
                    ${strike.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {uniqueStrikes.length} strikes available
            </p>
          </div>

          {/* Summary Info */}
          <div className="flex flex-col justify-center">
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-600">Current Stock Price</p>
              <p className="text-lg font-bold text-indigo-600">${currentPrice?.toFixed(2) || 'N/A'}</p>
              <p className="text-xs text-gray-600 mt-1">
                {selectedStrike === 'all' ? 'All strikes' : `Strike: $${selectedStrike}`}
              </p>
            </div>
          </div>
        </div>

        {/* Profit Calculations Toggle and Position Type */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <Button
              variant={showProfitCalculations ? "default" : "outline"}
              size="sm"
              onClick={() => setShowProfitCalculations(!showProfitCalculations)}
              className="text-xs"
            >
              <Calculator className="h-3 w-3 mr-1" />
              {showProfitCalculations ? 'Hide' : 'Show'} P&L Calculations
            </Button>
            
            {showProfitCalculations && (
              <div className="flex items-center space-x-2">
                <div className="text-xs font-medium text-gray-700">Position Type:</div>
                <Button
                  variant={positionType === 'long' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPositionType('long')}
                  className="text-xs"
                >
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Long (Buy)
                </Button>
                <Button
                  variant={positionType === 'short' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPositionType('short')}
                  className="text-xs"
                >
                  <TrendingDown className="h-3 w-3 mr-1" />
                  Short (Sell)
                </Button>
              </div>
            )}
            
            {showProfitCalculations && (
              <div className="text-xs text-gray-600">
                <span className="bg-green-50 px-2 py-1 rounded">ITM</span> = In The Money
              </div>
            )}
          </div>
          
          {/* Visualize P/L Button */}
          {onVisualizePL && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Create legs from selected options or use a default single option
                const legs = selectedOptions.length > 0 ? selectedOptions : [];
                onVisualizePL(legs);
                toast.success('Opening P/L Chart builder');
              }}
              className="text-xs bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
            >
              <LineChart className="h-3 w-3 mr-1" />
              Visualize P/L Chart
            </Button>
          )}
        </div>

        {/* Profit Calculation Legend */}
        {showProfitCalculations && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
              <Calculator className="h-4 w-4 mr-1 text-blue-600" />
              {positionType === 'long' ? 'Long' : 'Short'} Position P&L Calculations (Per Contract)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-700">
              {positionType === 'long' ? (
                <>
                  <div>
                    <p><strong>Break-Even:</strong> Price stock needs to reach to break even</p>
                    <p><strong>Max Loss:</strong> Maximum amount you can lose (premium paid)</p>
                  </div>
                  <div>
                    <p><strong>Scenario Columns:</strong> Profit if stock moves by % by expiration</p>
                    <p className="text-green-600"><strong>Calls:</strong> Profit when stock goes UP | <strong>Puts:</strong> Profit when stock goes DOWN</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p><strong>Break-Even:</strong> Price where you start losing money</p>
                    <p><strong>Max Profit:</strong> Premium received when selling the option</p>
                  </div>
                  <div>
                    <p><strong>Scenario Columns:</strong> Loss if stock moves against you</p>
                    <p className="text-red-600"><strong>Short Calls:</strong> Loss when stock goes UP | <strong>Short Puts:</strong> Loss when stock goes DOWN</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="mt-3 flex items-center justify-center text-sm text-gray-600">
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Loading options...
          </div>
        )}
      </div>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="calls" data-testid="calls-tab">Calls</TabsTrigger>
          <TabsTrigger value="puts" data-testid="puts-tab">Puts</TabsTrigger>
        </TabsList>

        <TabsContent value="calls" className="mt-4">
          {renderOptionTable(filterByStrike(optionData.calls || []), true)}
          {filterByStrike(optionData.calls || []).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No call options found for selected filters
            </div>
          )}
        </TabsContent>

        <TabsContent value="puts" className="mt-4">
          {renderOptionTable(filterByStrike(optionData.puts || []), false)}
          {filterByStrike(optionData.puts || []).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No put options found for selected filters
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OptionChain;
